/**
 * Login state machine (specs.md).
 * States S_LOGIN_*, events E_*, transitions. Guards are evaluated by the caller.
 */

export type LoginState =
  | 'S_LOGIN_SELECT_SERVICE'
  | 'S_LOGIN_SELECT_MEMBER'
  | 'S_LOGIN_BUILD_PATH'
  | 'S_LOGIN_CHECK_PAIRS'
  | 'S_LOGIN_NEED_MORE_PAIRS'
  | 'S_LOGIN_BUILD_CHALLENGE'
  | 'S_LOGIN_COLLECT_SIGNATURES'
  | 'S_LOGIN_PUBLISH_PROOF'
  | 'S_LOGIN_VERIFY_LOCAL'
  | 'S_LOGIN_SUCCESS'
  | 'S_LOGIN_FAILURE'
  | 'S_ERROR_RECOVERABLE'
  | 'S_ERROR_FATAL'
  | 'S_DIAGNOSTIC';

export type LoginEvent =
  | { type: 'E_SELECT_SERVICE'; serviceUuid: string }
  | { type: 'E_SELECT_MEMBER'; membreUuid: string }
  | { type: 'E_BACK' }
  | { type: 'E_PATH_OK' }
  | { type: 'E_PATH_INCOMPLETE' }
  | { type: 'E_PATH_INVALID' }
  | { type: 'E_PAIRS_OK' }
  | { type: 'E_PAIRS_INCOMPLETE' }
  | { type: 'E_CHALLENGE_READY' }
  | { type: 'E_SIGNATURES_COMPLETE' }
  | { type: 'E_PUBLISH_LOGIN_OK' }
  | { type: 'E_PUBLISH_LOGIN_PARTIAL' }
  | { type: 'E_LOCAL_VERDICT_ACCEPT' }
  | { type: 'E_LOCAL_VERDICT_REJECT' }
  | { type: 'E_DONE' }
  | { type: 'E_RETRY' }
  | { type: 'E_SYNC_NOW' }
  | { type: 'E_RESYNC' }
  | { type: 'E_ADD_PAIR' }
  | { type: 'E_OPEN_DIAGNOSTIC' }
  | { type: 'E_EXIT' };

export interface TransitionResult {
  nextState: LoginState;
  errorCode?: string;
}

const INITIAL: LoginState = 'S_LOGIN_SELECT_SERVICE';

/**
 * Compute next state from (state, event). Guards are caller responsibility.
 */
export function transition(
  state: LoginState,
  event: LoginEvent,
): TransitionResult {
  switch (state) {
    case 'S_LOGIN_SELECT_SERVICE':
      if (event.type === 'E_SELECT_SERVICE') {
        return { nextState: 'S_LOGIN_SELECT_MEMBER' };
      }
      if (event.type === 'E_BACK') {
        return { nextState: 'S_LOGIN_SELECT_SERVICE' };
      }
      break;

    case 'S_LOGIN_SELECT_MEMBER':
      if (event.type === 'E_SELECT_MEMBER') {
        return { nextState: 'S_LOGIN_BUILD_PATH' };
      }
      if (event.type === 'E_BACK') {
        return { nextState: 'S_LOGIN_SELECT_SERVICE' };
      }
      break;

    case 'S_LOGIN_BUILD_PATH':
      if (event.type === 'E_PATH_OK') {
        return { nextState: 'S_LOGIN_CHECK_PAIRS' };
      }
      if (event.type === 'E_PATH_INCOMPLETE' || event.type === 'E_PATH_INVALID') {
        return {
          nextState: 'S_ERROR_RECOVERABLE',
          errorCode: 'E_PATH_INCOMPLETE',
        };
      }
      if (event.type === 'E_BACK') {
        return { nextState: 'S_LOGIN_SELECT_MEMBER' };
      }
      break;

    case 'S_LOGIN_CHECK_PAIRS':
      if (event.type === 'E_PAIRS_OK') {
        return { nextState: 'S_LOGIN_BUILD_CHALLENGE' };
      }
      if (event.type === 'E_PAIRS_INCOMPLETE') {
        return { nextState: 'S_LOGIN_NEED_MORE_PAIRS' };
      }
      if (event.type === 'E_BACK') {
        return { nextState: 'S_LOGIN_BUILD_PATH' };
      }
      break;

    case 'S_LOGIN_NEED_MORE_PAIRS':
      if (event.type === 'E_ADD_PAIR') {
        return { nextState: 'S_LOGIN_SELECT_SERVICE' };
      }
      if (event.type === 'E_SYNC_NOW') {
        return { nextState: 'S_LOGIN_BUILD_PATH' };
      }
      if (event.type === 'E_BACK') {
        return { nextState: 'S_LOGIN_BUILD_PATH' };
      }
      break;

    case 'S_LOGIN_BUILD_CHALLENGE':
      if (event.type === 'E_CHALLENGE_READY') {
        return { nextState: 'S_LOGIN_COLLECT_SIGNATURES' };
      }
      if (event.type === 'E_BACK') {
        return { nextState: 'S_LOGIN_CHECK_PAIRS' };
      }
      break;

    case 'S_LOGIN_COLLECT_SIGNATURES':
      if (event.type === 'E_SIGNATURES_COMPLETE') {
        return { nextState: 'S_LOGIN_PUBLISH_PROOF' };
      }
      if (event.type === 'E_BACK') {
        return { nextState: 'S_LOGIN_BUILD_CHALLENGE' };
      }
      break;

    case 'S_LOGIN_PUBLISH_PROOF':
      if (event.type === 'E_PUBLISH_LOGIN_OK') {
        return { nextState: 'S_LOGIN_VERIFY_LOCAL' };
      }
      if (event.type === 'E_PUBLISH_LOGIN_PARTIAL') {
        return {
          nextState: 'S_ERROR_RECOVERABLE',
          errorCode: 'E_PUBLISH_LOGIN_PARTIAL',
        };
      }
      if (event.type === 'E_LOCAL_VERDICT_REJECT') {
        return { nextState: 'S_LOGIN_FAILURE' };
      }
      if (event.type === 'E_BACK') {
        return { nextState: 'S_LOGIN_COLLECT_SIGNATURES' };
      }
      break;

    case 'S_LOGIN_VERIFY_LOCAL':
      if (event.type === 'E_LOCAL_VERDICT_ACCEPT') {
        return { nextState: 'S_LOGIN_SUCCESS' };
      }
      if (event.type === 'E_LOCAL_VERDICT_REJECT') {
        return { nextState: 'S_LOGIN_FAILURE' };
      }
      break;

    case 'S_LOGIN_SUCCESS':
    case 'S_LOGIN_FAILURE':
      if (event.type === 'E_DONE') {
        return { nextState: 'S_LOGIN_SELECT_SERVICE' };
      }
      if (event.type === 'E_RETRY') {
        return { nextState: 'S_LOGIN_BUILD_PATH' };
      }
      if (event.type === 'E_RESYNC') {
        return { nextState: 'S_LOGIN_SELECT_SERVICE' };
      }
      if (event.type === 'E_BACK') {
        return { nextState: 'S_LOGIN_SELECT_SERVICE' };
      }
      break;

    case 'S_ERROR_RECOVERABLE':
      if (event.type === 'E_RETRY') {
        return { nextState: 'S_LOGIN_BUILD_PATH' };
      }
      if (event.type === 'E_SYNC_NOW') {
        return { nextState: 'S_LOGIN_BUILD_PATH' };
      }
      if (event.type === 'E_ADD_PAIR') {
        return { nextState: 'S_LOGIN_SELECT_SERVICE' };
      }
      if (event.type === 'E_OPEN_DIAGNOSTIC') {
        return { nextState: 'S_DIAGNOSTIC' };
      }
      if (event.type === 'E_BACK') {
        return { nextState: 'S_LOGIN_SELECT_SERVICE' };
      }
      break;

    case 'S_ERROR_FATAL':
      if (event.type === 'E_EXIT') {
        // État terminal - pas de transition possible
        return { nextState: 'S_ERROR_FATAL' };
      }
      break;

    case 'S_DIAGNOSTIC':
      if (event.type === 'E_BACK') {
        return { nextState: 'S_ERROR_RECOVERABLE' };
      }
      break;
  }

  return { nextState: state };
}

export function getInitialLoginState(): LoginState {
  return INITIAL;
}

export function isTerminal(state: LoginState): boolean {
  return state === 'S_LOGIN_SUCCESS' || state === 'S_LOGIN_FAILURE';
}

export function isErrorState(state: LoginState): boolean {
  return state === 'S_ERROR_RECOVERABLE' || state === 'S_ERROR_FATAL';
}

export function isFatalError(state: LoginState): boolean {
  return state === 'S_ERROR_FATAL';
}
